import React, { useState } from "react";
import { Divider, Grid, Typography } from "@material-ui/core";

import { errorMessages } from "constants/errorMessages";
import { snackbarTypes } from "constants/snackbar";

import { useSnackbar } from "contexts/SnackbarContext";
import { useUserContext } from "contexts/UserContext";

import * as pdfReportsService from "api/services/pdfReportService";
import * as SummaryPayload from "api/services/summaryService";

import FinancialEntry from "components/municipal/financialSummary/FinancialEntry";
import ListPageWrapper from "components/ListPageWrapper";
import TitleRow from "components/municipal/financialSummary/TitleRow";
import TotalRow from "components/municipal/financialSummary/TotalRow";
import useStyles from "components/municipal/financialSummary/financialTableStyles";

import { useIsMounted } from "hooks/useIsMounted";

import { exportFinancialSummary } from "utils/export";
import { printFile } from "utils/pdf";

import FilterActionsBar from "../FilterActionsBar";
import { useSummaryStyles } from "../hooks/summaryStylesHook";
import { useYearsRange } from "../hooks/yearsRangeHooks";
// Used for JSDocs
// eslint-disable-next-line import/named, no-unused-vars
import { useProjectFinancialsData } from "./projectFinancialsHooks";

/**
 * Financial summary page for municipal users.
 *
 * @returns {React.Component} Financial Summary Page component.
 */
const FinancialSummaryPage = () => {
  const { user } = useUserContext();
  const classes = useStyles();
  const summaryClasses = useSummaryStyles();
  const { showSnackbar } = useSnackbar();

  const mounted = useIsMounted();

  const { data: years } = useYearsRange("financial");

  const [payload, setPayload] = useState(/** @type {SummaryPayload} */ ({}));
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const { data: financials, isFetched } = useProjectFinancialsData(payload);

  const getPdfFile = async () => {
    try {
      const { data } = await pdfReportsService.getSummaryFinancials(
        user.municipalityId,
        payload
      );
      return data;
    } catch (error) {
      console.error(error);
      showSnackbar(errorMessages.generic, snackbarTypes.error);
    }
    return null;
  };

  const handlePrint = async () => {
    setIsLoadingFile(true);

    const pdfFile = await getPdfFile();

    if (!mounted.current) {
      return;
    }

    if (pdfFile) {
      printFile(pdfFile);
    }

    setIsLoadingFile(false);
  };

  return (
    <ListPageWrapper>
      <Grid container direction="column" spacing={2} wrap="nowrap">
        <Grid item>
          <Typography className={summaryClasses.title} variant="h2">
            Financial Summary
          </Typography>
        </Grid>
        <Grid item>
          <FilterActionsBar
            years={years}
            onFilter={({ from, to }) =>
              setPayload({
                municipalityId: user.municipalityId,
                yearStart: from,
                yearEnd: to,
              })
            }
            onDownload={() => {
              try {
                exportFinancialSummary(financials.years);
              } catch (error) {
                console.error(error);

                showSnackbar(
                  errorMessages.creatingSpreadsheet,
                  snackbarTypes.error
                );
              }
            }}
            onPrint={handlePrint}
            disableOptionalButtons={
              !isFetched || !financials?.years?.length || isLoadingFile
            }
            showPrintLoading={isLoadingFile}
          />
        </Grid>
        {isFetched && !financials?.years?.length && (
          <Grid item>
            <Typography>
              No results found for the selected reporting period
            </Typography>
          </Grid>
        )}
        {!!financials?.years?.length && (
          <Grid
            container
            direction="column"
            className={classes.margins}
            // Dynamic width set as style
            style={{
              minWidth: `${
                25 + 12.5 * (financials?.years?.length || 0) + 16
              }rem`,
            }}
            wrap="nowrap"
          >
            <Grid
              container
              direction="row"
              wrap="nowrap"
              className={classes.firstRow}
              align="right"
            >
              <Grid item className={classes.firstColumnCell} />
              {financials?.years &&
                financials?.years?.map(({ year }) => (
                  <Grid item className={classes.cell} key={year}>
                    <Typography variant="h5" className={classes.header}>
                      {year}
                    </Typography>
                  </Grid>
                ))}
              <Grid
                className={classes.middleColumnSpacing}
                style={{ marginRight: 4 }}
              />
              <Grid item className={classes.lastColumnCell} align="right">
                <Typography variant="h5" className={classes.header}>
                  Cumulative (2005 - {financials && payload.yearEnd})
                </Typography>
              </Grid>
            </Grid>
            <Divider className={summaryClasses.divider} />
            <TitleRow
              displayName="Opening balance"
              classes={classes}
              financialDataFieldName="openBalance"
              financialData={financials}
              underlined={false}
            />

            <TitleRow displayName="Revenues" classes={classes} />
            <FinancialEntry
              displayName="Received from AMO (Including Receivables)"
              financialDataFieldName="allocation"
              financialData={financials}
              classes={classes}
              isNegative={false}
            />
            <FinancialEntry
              displayName="Interest earned"
              financialDataFieldName="interestEarned"
              financialData={financials}
              classes={classes}
              isNegative={false}
            />
            <FinancialEntry
              displayName="Proceeds from the Disposal of Assets"
              financialDataFieldName="proceedsFromDisposal"
              financialData={financials}
              classes={classes}
              isNegative={false}
            />

            <TotalRow
              displayName="Net Revenues"
              financialDataFieldName="netRevenues"
              financialData={financials}
              classes={classes}
            />

            <TitleRow displayName="Transfers" classes={classes} />
            <FinancialEntry
              displayName="Received from another municipality"
              financialDataFieldName="transferredIn"
              financialData={financials}
              classes={classes}
              isNegative={false}
            />
            <FinancialEntry
              displayName="Transferred to another municipality"
              financialDataFieldName="transferredOut"
              financialData={financials}
              classes={classes}
            />

            <TotalRow
              displayName="Net Transfers"
              financialDataFieldName="netTransfers"
              financialData={financials}
              classes={classes}
            />

            <TitleRow displayName="Expenditures" classes={classes} />
            <FinancialEntry
              displayName="Broadband Connectivity"
              financialDataFieldName="broadband"
              financialData={financials}
              classes={classes}
              isNegative
            />
            <FinancialEntry
              displayName="Brownfield Redevelopment"
              financialDataFieldName="brownfield"
              financialData={financials}
              classes={classes}
              isNegative
            />
            <FinancialEntry
              displayName="Capacity Building"
              financialDataFieldName="capacityBldg"
              financialData={financials}
              classes={classes}
              isNegative
            />
            <FinancialEntry
              displayName="Community Energy Systems"
              financialDataFieldName="commEnergy"
              financialData={financials}
              classes={classes}
              isNegative
            />
            <FinancialEntry
              displayName="Culture"
              financialDataFieldName="culture"
              financialData={financials}
              classes={classes}
              isNegative
            />
            <FinancialEntry
              displayName="Drinking Water"
              financialDataFieldName="water"
              financialData={financials}
              classes={classes}
              isNegative
            />
            <FinancialEntry
              displayName="Fire Stations"
              financialDataFieldName="fireStation"
              financialData={financials}
              classes={classes}
              isNegative
              dontShowBeforeYear={2021}
            />
            <FinancialEntry
              displayName="Local Roads and Bridges"
              financialDataFieldName="roadsBridges"
              financialData={financials}
              classes={classes}
              isNegative
            />
            <FinancialEntry
              displayName="Public Transit"
              financialDataFieldName="transit"
              financialData={financials}
              classes={classes}
              isNegative
            />
            <FinancialEntry
              displayName="Recreation"
              financialDataFieldName="recreation"
              financialData={financials}
              classes={classes}
              isNegative
            />
            <FinancialEntry
              displayName="Resilience"
              financialDataFieldName="disaster"
              financialData={financials}
              classes={classes}
              isNegative
            />
            <FinancialEntry
              displayName="Regional and Local Airports"
              financialDataFieldName="airport"
              financialData={financials}
              classes={classes}
              isNegative
            />
            <FinancialEntry
              displayName="Short-line Rail"
              financialDataFieldName="shortline"
              financialData={financials}
              classes={classes}
              isNegative
            />
            <FinancialEntry
              displayName="Short-sea Shipping"
              financialDataFieldName="shortsea"
              financialData={financials}
              classes={classes}
              isNegative
            />
            <FinancialEntry
              displayName="Solid Waste"
              financialDataFieldName="solidWaste"
              financialData={financials}
              classes={classes}
              isNegative
            />
            <FinancialEntry
              displayName="Sports"
              financialDataFieldName="sport"
              financialData={financials}
              classes={classes}
              isNegative
            />
            <FinancialEntry
              displayName="Tourism"
              financialDataFieldName="tourism"
              financialData={financials}
              classes={classes}
              isNegative
            />
            <FinancialEntry
              displayName="Wastewater"
              financialDataFieldName="wastewater"
              financialData={financials}
              classes={classes}
              isNegative
            />

            <TotalRow
              displayName="Net Expenditures"
              financialDataFieldName="netExpenditures"
              financialData={financials}
              classes={classes}
              isNegative
            />

            <TitleRow
              displayName="Closing balance"
              classes={classes}
              financialDataFieldName="closeBalance"
              financialData={financials}
              underlined={false}
            />
          </Grid>
        )}
      </Grid>
    </ListPageWrapper>
  );
};

export default FinancialSummaryPage;
